import React from "react";
import { motion } from "framer-motion";

/**
 * Simplified Brazil map (schematic). Each state has approximate SVG position.
 * Active states show a pulsing "radar" dot with the number of projects.
 *
 * NOT a geographically precise map — it's a stylized minimal representation
 * that fits the premium/editorial aesthetic of the site.
 */

// Positions calibrated for a 640×720 viewBox
const STATE_POSITIONS = {
  AC: { x: 100, y: 340, label: "Acre" },
  AM: { x: 180, y: 260, label: "Amazonas" },
  RR: { x: 220, y: 130, label: "Roraima" },
  RO: { x: 180, y: 380, label: "Rondônia" },
  PA: { x: 300, y: 260, label: "Pará" },
  AP: { x: 320, y: 150, label: "Amapá" },
  TO: { x: 360, y: 340, label: "Tocantins" },
  MA: { x: 400, y: 240, label: "Maranhão" },
  PI: { x: 440, y: 290, label: "Piauí" },
  CE: { x: 490, y: 220, label: "Ceará" },
  RN: { x: 540, y: 240, label: "Rio Grande do Norte" },
  PB: { x: 550, y: 275, label: "Paraíba" },
  PE: { x: 530, y: 305, label: "Pernambuco" },
  AL: { x: 545, y: 335, label: "Alagoas" },
  SE: { x: 525, y: 355, label: "Sergipe" },
  BA: { x: 470, y: 380, label: "Bahia" },
  MT: { x: 260, y: 400, label: "Mato Grosso" },
  MS: { x: 290, y: 490, label: "Mato Grosso do Sul" },
  DF: { x: 380, y: 420, label: "Distrito Federal" },
  GO: { x: 350, y: 440, label: "Goiás" },
  MG: { x: 420, y: 470, label: "Minas Gerais" },
  ES: { x: 490, y: 490, label: "Espírito Santo" },
  RJ: { x: 445, y: 530, label: "Rio de Janeiro" },
  SP: { x: 380, y: 540, label: "São Paulo" },
  PR: { x: 340, y: 585, label: "Paraná" },
  SC: { x: 340, y: 630, label: "Santa Catarina" },
  RS: { x: 300, y: 675, label: "Rio Grande do Sul" },
};

export default function BrazilRadarMap({ states = {} }) {
  const entries = Object.entries(STATE_POSITIONS);

  return (
    <div className="relative w-full" data-testid="brazil-radar-map">
      <svg viewBox="0 0 640 750" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
        {/* Stylized Brazil outline (simplified silhouette) */}
        <defs>
          <linearGradient id="brasil-fill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.02)" />
          </linearGradient>
        </defs>
        <path
          d="M 220 120 C 260 100, 320 110, 360 140 L 420 160 L 470 200 L 520 210 L 555 240 L 570 290 L 555 340 L 540 380 L 500 410 L 490 460 L 500 500 L 470 540 L 440 570 L 410 590 L 380 600 L 360 615 L 340 640 L 320 680 L 290 700 L 260 690 L 250 660 L 260 620 L 230 590 L 200 560 L 170 520 L 160 470 L 180 420 L 160 380 L 130 350 L 110 310 L 130 270 L 160 240 L 170 200 L 200 160 Z"
          fill="url(#brasil-fill)"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="1.5"
        />

        {entries.map(([uf, pos]) => {
          const st = states[uf] || {};
          if (!st.active) {
            // inactive state: tiny dim dot
            return <circle key={uf} cx={pos.x} cy={pos.y} r="2" fill="rgba(255,255,255,0.1)" data-testid={`br-state-inactive-${uf}`} />;
          }
          const color = st.color || "#FF5E00";
          const count = st.count || 0;
          return (
            <g key={uf} data-testid={`br-state-active-${uf}`}>
              {/* Radar ripple animation */}
              <motion.circle
                cx={pos.x} cy={pos.y} r={6}
                stroke={color} strokeWidth="1.5" fill="none"
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: [1, 3.5], opacity: [0.6, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut" }}
                style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}
              />
              <motion.circle
                cx={pos.x} cy={pos.y} r={6}
                stroke={color} strokeWidth="1" fill="none"
                initial={{ scale: 1, opacity: 0.4 }}
                animate={{ scale: [1, 5], opacity: [0.4, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: 0.8 }}
                style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}
              />
              {/* Solid dot */}
              <circle cx={pos.x} cy={pos.y} r="5" fill={color} />
              {/* Label */}
              <text x={pos.x} y={pos.y - 12} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">{uf}</text>
              {count > 0 && (
                <>
                  <rect x={pos.x + 8} y={pos.y - 10} width={String(count).length * 7 + 10} height="16" rx="8" fill={color} />
                  <text x={pos.x + 8 + (String(count).length * 7 + 10) / 2} y={pos.y + 2} textAnchor="middle" fill="#0B0B0B" fontSize="10" fontWeight="800">{count}</text>
                </>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
